import { cn } from '@nexo/ui';
import { useCallback, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router';
import { FullScreenSpinner } from '../features/auth/RequireAuth';
import { ChannelSidebar } from '../features/channels/ChannelSidebar';
import { CreateChannelDialog } from '../features/channels/CreateChannelDialog';
import { NewDmDialog } from '../features/channels/NewDmDialog';
import { SearchProvider } from '../features/search/SearchDialog';
import { TaskDrawer } from '../features/tasks/TaskDrawer';
import { UserCardHost } from '../features/teams/UserCard';
import { FileViewer } from '../features/viewer/FileViewer';
import { useIdlePresence } from '../features/status/useIdlePresence';
import { CallDock } from '../features/calls/CallControls';
import { MembersDialog } from '../features/teams/MembersDialog';
import { TeamRail } from '../features/teams/TeamRail';
import { TeamSettingsDialog } from '../features/teams/TeamSettingsDialog';
import { useChannels, useMembers, useRoles, useTasks, useTeams } from '../lib/queries';
import { useTeamRealtime } from '../realtime/useTeamRealtime';
import { UpdateBanner } from '../components/UpdateBanner';
import { AppTopBar } from './AppTopBar';
import { RightPanelView, UtilityRail } from './RightPanel';
import {
  TeamContext,
  type RightPanel,
  type TaskDraft,
  type TeamContextValue,
  type TeamDialog,
} from './TeamContext';

const LAST_TEAM_KEY = 'nexo.lastTeam';
const RIGHT_PANEL_KEY = 'nexo.rightPanel';

export const rememberedTeamId = () => localStorage.getItem(LAST_TEAM_KEY);

function initialPanel(): RightPanel | null {
  // On narrow screens the panel is an overlay, so it starts closed.
  if (!window.matchMedia('(min-width: 1280px)').matches) return null;
  const stored = localStorage.getItem(RIGHT_PANEL_KEY);
  if (stored === 'none') return null;
  return (
    (['members', 'tasks', 'files', 'integrations'] as const).find((p) => p === stored) ?? 'members'
  );
}

/**
 * Workspace layout: dark top bar, then team rail | sidebar | main panel | contextual panel |
 * utility rail. On small screens the sidebar and the right panel become drawers.
 */
export function TeamShell() {
  const { teamId = '' } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const teams = useTeams();
  const members = useMembers(teamId);
  const channels = useChannels(teamId);
  const roles = useRoles(teamId);
  useTasks(teamId);
  const online = useTeamRealtime(teamId, channels.data);
  useIdlePresence();

  // The mobile drawer remembers the page it was opened on, so navigating closes it.
  const [sidebarOpenAt, setSidebarOpenAt] = useState<string | null>(null);
  const sidebarOpen = sidebarOpenAt === location.pathname;
  const [rightPanel, setRightPanelState] = useState<RightPanel | null>(initialPanel);
  const [taskDraft, setTaskDraft] = useState<TaskDraft | null>(null);
  const [dialog, setDialog] = useState<TeamDialog | null>(null);
  const [threadId, setThreadId] = useState<string | null>(null);

  const team = teams.data?.find((t) => t.id === teamId);
  if (team) localStorage.setItem(LAST_TEAM_KEY, team.id);

  // Deep link from notifications: ?task=<id> opens that task.
  const taskParam = searchParams.get('task');
  const activeDraft = useMemo<TaskDraft | null>(
    () => taskDraft ?? (taskParam ? { mode: 'edit', taskId: taskParam } : null),
    [taskDraft, taskParam],
  );
  const closeTask = () => {
    setTaskDraft(null);
    if (taskParam) {
      setSearchParams(
        (params) => {
          params.delete('task');
          return params;
        },
        { replace: true },
      );
    }
  };

  const setRightPanel = useCallback((panel: RightPanel | null) => {
    // Threads and pins depend on the conversation: don't restore them on the next visit.
    if (panel !== 'thread' && panel !== 'pins')
      localStorage.setItem(RIGHT_PANEL_KEY, panel ?? 'none');
    setRightPanelState(panel);
  }, []);
  const openThread = useCallback((messageId: string) => {
    setThreadId(messageId);
    setRightPanelState('thread');
  }, []);

  const value = useMemo<TeamContextValue | null>(() => {
    if (!team || !members.data || !channels.data) return null;
    const byId = new Map(members.data.map((m) => [m.id, m]));
    const roleList = roles.data ?? [];
    const roleById = new Map(roleList.map((r) => [r.id, r]));
    return {
      team,
      members: members.data,
      roles: roleList,
      roleById: (id) => roleById.get(id),
      channels: channels.data,
      online,
      memberById: (id) => byId.get(id),
      openTask: setTaskDraft,
      openDialog: setDialog,
      openSidebar: () => setSidebarOpenAt(location.pathname),
      rightPanel,
      setRightPanel,
      threadId,
      openThread,
    };
  }, [
    team,
    members.data,
    roles.data,
    channels.data,
    online,
    rightPanel,
    setRightPanel,
    threadId,
    openThread,
    location.pathname,
  ]);

  if (teams.data && !team) {
    localStorage.removeItem(LAST_TEAM_KEY);
    return <Navigate to="/app/home" replace />;
  }
  if (!value) return <FullScreenSpinner />;

  return (
    <TeamContext.Provider value={value}>
      <SearchProvider>
        <div className="flex h-full flex-col bg-canvas">
          <UpdateBanner />
          <AppTopBar />
          <div className="flex min-h-0 flex-1 md:gap-2 md:pr-2 md:pb-2">
            {/* Left: rail + sidebar (drawer on mobile) */}
            <div
              className={cn(
                'fixed inset-y-0 left-0 z-40 flex bg-canvas transition-transform duration-200 md:static md:translate-x-0',
                sidebarOpen ? 'translate-x-0 shadow-float' : '-translate-x-full',
              )}
            >
              <TeamRail />
              <ChannelSidebar />
            </div>
            {sidebarOpen && (
              <div
                className="fixed inset-0 z-30 bg-black/40 backdrop-blur-[1px] md:hidden"
                onClick={() => setSidebarOpenAt(null)}
              />
            )}

            <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-surface md:rounded-2xl md:border md:border-border md:shadow-panel">
              <Outlet />
            </main>

            {rightPanel && (
              <>
                <div
                  className="fixed inset-0 z-30 bg-black/40 xl:hidden"
                  onClick={() => setRightPanel(null)}
                />
                <aside className="fixed inset-y-0 right-0 z-40 w-[22rem] max-w-[92vw] animate-slide-in overflow-hidden border-l border-border bg-surface shadow-float xl:static xl:z-auto xl:w-[20rem] xl:animate-none xl:rounded-2xl xl:border xl:shadow-panel">
                  <RightPanelView panel={rightPanel} />
                </aside>
              </>
            )}
            <UtilityRail />
          </div>
        </div>

        <TaskDrawer draft={activeDraft} onClose={closeTask} />
        <FileViewer />
        <UserCardHost />
        <CallDock />
        <CreateChannelDialog
          open={dialog === 'channel' || dialog === 'voice'}
          initialMode={dialog === 'voice' ? 'voice' : 'text'}
          onClose={() => setDialog(null)}
        />
        <NewDmDialog open={dialog === 'dm'} onClose={() => setDialog(null)} />
        <MembersDialog open={dialog === 'members'} onClose={() => setDialog(null)} />
        {dialog === 'settings' && (
          <TeamSettingsDialog team={value.team} open onClose={() => setDialog(null)} />
        )}
      </SearchProvider>
    </TeamContext.Provider>
  );
}
